import { EProductTourTargets } from '@constants';
import { IPost } from '@sdk/contracts';
import { Empty } from 'antd';
import { useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { WidgetTitle, WidgetWrapper } from '../shared/WidgetShared.styles';

import { PostItem } from './PostItem';
import { PostViewer } from './PostViewer';
import { PostsWrapper, TitleWrapper } from './PostWidget.styles';
import { TopicSelector } from './TopicSelector';

import { LoadingCircleSpinner } from '@/components/Loaders';
import { usePostsList } from '@/store';

const LOADER_SIZE = 40;

export const PostWidget: React.FC = () => {
  const { posts = [], isPending, loadMorePosts, selectedTopic, setSelectedTopic } = usePostsList();
  const { t } = useTranslation('StudentDashboardTranslations');
  const listRef = useRef<HTMLDivElement>(null);

  const onScroll = useCallback(() => {
    if (listRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = listRef.current;
      if (scrollTop + clientHeight >= scrollHeight - 10 && !isPending) {
        loadMorePosts();
      }
    }
  }, [isPending, loadMorePosts]);

  const shouldBeSetAsFeatured = useCallback((post: IPost, index: number) => {
    const isFirstOrSecond = index === 0 || index === 1;
    return isFirstOrSecond && post.isFeatured;
  }, []);

  const handleTopicChange = (topic?: string) => {
    setSelectedTopic(topic);
  };

  return (
    <WidgetWrapper id={EProductTourTargets.POSTS_WIDGET}>
      <TitleWrapper>
        <WidgetTitle>{t('NEWS')}</WidgetTitle>
        <TopicSelector value={selectedTopic} onChange={handleTopicChange} />
      </TitleWrapper>

      <PostsWrapper ref={listRef} onScroll={onScroll}>
        {posts.length === 0 && <Empty description={t('NO_NEWS_TO_SHOW')} />}
        {posts.map((post, index) => (
          <PostItem key={post._id} post={post} isFeatured={shouldBeSetAsFeatured(post, index)} />
        ))}
        {isPending && <LoadingCircleSpinner size={LOADER_SIZE} />}
      </PostsWrapper>
      <PostViewer />
    </WidgetWrapper>
  );
};
